import { EntityTypes } from '@/store/modules/module-tree/enums/module-tree.enums';
import Vue from 'vue';
import { default as Router, default as VueRouter } from 'vue-router';
import {
  CasbinRouteGuard,
  GeneralRouteGuard,
  SetContext,
  UserAttributeRouteGuard
} from './router.guard';

export interface CustomNavLink {
  label: string;
  to?: {
    name: string;
    module: string;
    query?: Record<string, string>;
    instanceType?: EntityTypes;
  };
  url?: string;
  class?: string;
}

Vue.use(Router);

// Create as extendable array for paths that can skip handler validation
export const NON_VALIDATION_PATHS: string[] = ['/page-not-found'];

/**
 * Routes are being lazy loaded meaning there's route level
 * code splitting. This tells webpack to generate a
 * separate chunk, about.[hash].js, for example for the
 * route 'about'.
 */
// @ts-ignore
const router: VueRouter = new Router({
  linkExactActiveClass: 'exact-active',
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      name: 'LandingPage',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'non_module',
        isPublic: false
      },
      component: () => import('./views/LandingPage/LandingPage.vue')
    },
    {
      path: '/short-courses/:slug',
      name: 'ShortCourses',
      meta: {
        isCourseDetailsPage: true,
        module: 'non_module'
      },
      component: () => import('./views/PublicPages/ShortCourseDetailsPage.vue')
    },
    {
      path: '/products',
      name: 'products',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'non_module',
        isPublic: false
      },
      component: () => import('./views/Home/HomePage.vue')
    },
    {
      path: '/account',
      name: 'UserProfile',
      meta: {
        authRequired: true,
        module: 'account',
        isPublic: false
      },
      component: () => import('./views/Profile/UserProfile.vue')
    },
    {
      path: '/orders',
      name: 'Orders',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'my_orders',
        isPublic: false
      },
      component: () => import('./views/Order/MyOrdersPage.vue')
    },
    {
      path: '/orders/:id',
      name: 'Order',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'my_orders',
        isPublic: false
      },
      component: () => import('./views/Order/OrderPage.vue')
    },
    {
      path: '/application/:appId/products',
      name: 'Products',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'products',
        isPublic: false
      },
      component: () => import('./views/Product/ProductPage.vue')
    },
    {
      path: '/checkout/products/:productId',
      name: 'ProductCheckout',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'products',
        isPublic: false
      },
      component: () => import('./views/Product/ProductCheckoutPage.vue')
    },
    {
      path: '/checkout/short-courses/:slug',
      name: 'ShortCourseCheckout',
      meta: {
        requiresConditionalAuth: true,
        module: 'short_courses'
      },
      component: () =>
        import('./views/Product/ShortCourses/ShortCoursesCheckoutPage.vue')
    },
    {
      path: '/admin/product-management',
      name: 'admin-product-management',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'product_administration',
        isPublic: false
      },
      component: () =>
        import('./views/ProductManagement/ProductManagementPage.vue')
    },
    {
      path: '/admin/third-party-management',
      name: 'third-party-management',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'third_party_access_management',
        isPublic: false
      },
      component: () =>
        import(
          './views/AdminThirdPartyManagement/AdminThirdPartyManagement.vue'
        )
    },
    {
      path: '/admin/third-party-management/fhir-settings',
      name: 'fhir-settings',
      meta: {
        authRequired: true,
        module: 'third_party_access_management-fhir'
      },
      component: () =>
        import('./views/AdminThirdPartyManagement/FHIRSettingsManagement.vue')
    },
    {
      path: '/admin/third-party-management/fhir-clients/new',
      name: 'fhir-client-create',
      meta: {
        authRequired: true,
        module: 'third_party_access_management-fhir'
      },
      component: () =>
        import('./views/AdminThirdPartyManagement/FHIRAddClient.vue')
    },
    {
      path: '/admin/third-party-management/fhir-clients/:id',
      name: 'fhir-client-detail',
      meta: {
        authRequired: true,
        module: 'third_party_access_management-fhir'
      },
      component: () =>
        import('./views/AdminThirdPartyManagement/FHIRClientDetail.vue')
    },
    {
      path: '/journals',
      name: 'Journals',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'journals',
        isPublic: false
      },
      component: () => import('./views/Journal/JournalPage.vue')
    },
    {
      path: '/admin/roles-and-permissions',
      name: 'roles-and-permissions',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'roles_and_permissions',
        isPublic: false
      },
      component: () =>
        import('./views/RolesAndPermissions/RolesAndPermissions.vue')
    },
    {
      path: '/admin/roles/:roleId',
      name: 'view-role-permissions',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'roles_and_permissions',
        isPublic: false
      },
      component: () => import('./views/RolesAndPermissions/RolePermissions.vue')
    },
    {
      path: '/admin/roles/:roleId/assigned',
      name: 'view-resources-assigned',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'roles_and_permissions-view_assigned_resources',
        isPublic: false
      },
      component: () => import('./views/RolesAndPermissions/RoleResources.vue')
    },
    {
      path: '/admin/exceptions/:moduleName',
      name: 'view-module-exceptions',
      meta: {
        authRequired: false,
        checkUserAttribute: true,
        module: 'roles_and_permissions',
        isPublic: false
      },
      component: () =>
        import('./views/RolesAndPermissions/ViewModuleExceptions.vue')
    },
    {
      path: '/admin/users/:user_id',
      name: 'view-user-roles-and-exceptions',
      meta: {
        authRequired: false,
        checkUserAttribute: true,
        module: 'roles_and_permissions'
      },
      component: () =>
        import(
          './views/RolesAndPermissions/components/ViewUserRolesAndExceptions.vue'
        )
    },
    {
      path: '/admin/groups/:group_id',
      name: 'view-group-roles-and-exceptions',
      meta: {
        authRequired: false,
        checkUserAttribute: true,
        module: 'roles_and_permissions'
      },
      component: () =>
        import(
          './views/RolesAndPermissions/components/ViewGroupRolesAndExceptions.vue'
        )
    },
    {
      path: '/admin/user-management',
      name: 'admin-user-management',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'user_administration',
        isPublic: false
      },
      component: () =>
        import('./views/AdminUserManagement/AdminUserManagement.vue')
    },
    {
      path: '/admin/tnc-management',
      name: 'terms-and-conditiions-management',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'terms_and_conditions',
        isPublic: false
      },
      component: () =>
        import(
          './views/TermsAndConditionsManagement/TermsAndConditionsManagement.vue'
        )
    },
    {
      path: '/admin/group-management',
      name: 'admin-group-management',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'group_administration',
        instanceType: EntityTypes.GROUP,
        isPublic: false
      },
      component: () =>
        import('./views/AdminGroupManagement/AdminGroupManagement.vue')
    },
    {
      path: '/admin/create-group',
      name: 'admin-create-group',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'group_administration',
        isPublic: false
      },
      component: () => import('./views/AdminCreateGroup/AdminCreateGroup.vue')
    },
    {
      path: '/admin/group/:groupId',
      name: 'admin-view-group',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'group_administration',
        instanceType: EntityTypes.GROUP,
        isPublic: false
      },
      component: () => import('./views/AdminViewGroup/AdminViewGroup.vue')
    },
    {
      path: '/admin/group/:groupId/settings',
      name: 'admin-group-settings',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'group_administration',
        instanceType: EntityTypes.GROUP,
        isPublic: false
      },
      component: () =>
        import('./views/AdminGroupSettings/AdminGroupSettings.vue')
    },
    {
      path: '/admin/create-subgroup/:parentGroupId/:isProtected',
      name: 'admin-create-subgroup',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'group_administration',
        instanceType: EntityTypes.GROUP,
        isPublic: false
      },
      component: () =>
        import('./views/AdminCreateSubGroup/AdminCreateSubGroup.vue')
    },
    {
      path: '/admin/transactions',
      name: 'admin-audit-log',
      meta: {
        authRequired: true,
        checkUserAttribute: true,
        module: 'transactions',
        isPublic: false
      },
      component: () => import('./views/AdminAuditLog/AdminAuditLog.vue')
    },
    {
      path: '/about',
      name: 'about',
      meta: {
        module: 'non_module'
      },
      component: () => import('./views/About.vue')
    },
    /**
     * JTMS Routes
     */
    {
      path: '/admin/jtms',
      component: () => import('./views/JTMS/JTMSAppWrapper.vue'),
      children: [
        {
          path: '',
          redirect: 'dashboard',
          name: 'jtms-dashboard',
          meta: {
            authRequired: true,
            module: 'jtms_administrator'
          },
          component: () => import('./views/JTMS/Dashboard/JTMSDashboard.vue'),
          children: [
            {
              path: 'dashboard',
              name: 'dashboard-overview',
              meta: {
                authRequired: true,
                module: 'jtms_administrator'
              },
              component: () =>
                import(
                  './views/JTMS/Dashboard/DashboardOverview/JTMSDashboardOverview.vue'
                )
            },
            {
              path: 'courses/view-course/status/:courseStatus?',
              name: 'view-courses-by-status',
              meta: {
                authRequired: true,
                module: 'jtms_administrator'
              },
              component: () =>
                import('./views/JTMS/Dashboard/ViewCoursesByStatus.vue')
            },
            {
              path: 'courses/create-course',
              name: 'create-courses',
              meta: {
                authRequired: true,
                module: 'jtms_administrator'
              },
              component: () =>
                import(
                  './views/JTMS/Dashboard/CreateCourses/CreateCoursesPage.vue'
                )
            },
            {
              path: 'courses/view-course/:courseId',
              name: 'view-courses',
              meta: {
                authRequired: true,
                module: 'jtms_administrator'
              },
              component: () =>
                import('./views/JTMS/Dashboard/ViewCourses/ViewCourses.vue')
            },
            {
              path: 'courses/:courseId/settings',
              name: 'course-settings',
              meta: {
                authRequired: true,
                module: 'jtms_administrator'
              },
              component: () =>
                import(
                  './views/JTMS/Dashboard/EditCourses/EditCourseSettings.vue'
                )
            }
          ]
        },
        {
          path: 'payments',
          name: 'jtms-payments',
          meta: {
            authRequired: true,
            module: 'jtms-payments'
          },
          component: () => import('./views/JTMS/Payments/JTMSPayments.vue')
        },
        {
          path: 'discounts',
          name: 'jtms-discounts',
          meta: {
            authRequired: true,
            module: 'jtms-discounts'
          },
          component: () => import('./views/JTMS/Discounts/JTMSDiscounts.vue')
        },
        {
          path: 'trainer-accreditations',
          name: 'jtms-accreditation',
          meta: {
            authRequired: true,
            module: 'jtms-accreditation'
          },
          component: () => import('./views/PageInProgress.vue')
        },
        {
          path: 'course-templates',
          name: 'jtms-course_template',
          meta: {
            authRequired: true,
            module: 'jtms-course_template'
          },
          component: () => import('./views/PageInProgress.vue')
        },
        {
          path: 'course-attributes',
          name: 'jtms-course_attribute',
          meta: {
            authRequired: true,
            module: 'jtms-course_attribute'
          },
          component: () =>
            import('./views/JTMS/CourseAttributes/JTMSCourseAttribute.vue')
        },
        {
          path: 'course-modules',
          name: 'jtms-course_module',
          meta: {
            authRequired: true,
            module: 'jtms-course_module'
          },
          component: () => import('./views/JTMS/Modules/JTMSModule.vue')
        },
        {
          path: 'course-license-template',
          name: 'jtms-course_license_template',
          meta: {
            authRequired: true,
            module: 'jtms-course_license_template'
          },
          component: () =>
            import(
              './views/JTMS/CourseLicenseTemplate/JTMSCourseLicenseTemplate.vue'
            )
        },
        {
          path: 'registration-question-templates',
          name: 'jtms-registration_question_template',
          meta: {
            authRequired: true,
            module: 'jtms-registration_question_template'
          },
          component: () => import('./views/PageInProgress.vue')
        },
        {
          path: 'tasks',
          name: 'jtms-tasks',
          meta: {
            authRequired: true,
            module: 'jtms-tasks'
          },
          component: () => import('./views/PageInProgress.vue')
        },
        {
          path: 'certificate',
          name: 'jtms-certificate',
          meta: {
            authRequired: true,
            module: 'jtms-certificate'
          },
          component: () => import('./views/PageInProgress.vue')
        },
        {
          path: 'reports',
          name: 'jtms-reports',
          meta: {
            authRequired: true,
            module: 'jtms-reports'
          },
          component: () => import('./views/PageInProgress.vue')
        }
      ]
    },
    /**
     * JBIC Routes
     */
    {
      path: '/admin/jbic',
      name: 'jbic-management',
      meta: {
        authRequired: true,
        module: 'jbic_management-groups',
        instanceType: EntityTypes.GROUP
      },
      component: () => import('./views/JBIC/JBICManagement.vue')
    },
    {
      path: '/admin/jbic/group/:groupId',
      name: 'jbic-group',
      meta: {
        authRequired: true,
        module: 'jbic_management-groups',
        instanceType: EntityTypes.GROUP
      },
      component: () => import('./views/JBIC/GroupDetails.vue')
    },
    {
      path: '/admin/jbic/create-event/:groupId/:changeType/:eventId',
      name: 'jbic-create-event',
      meta: {
        authRequired: true,
        module: 'jbic_management-groups',
        instanceType: EntityTypes.GROUP
      },
      component: () => import('./views/JBIC/CreateEvent.vue')
    },
    {
      path: '/admin/jbic/event-view/:eventId',
      name: 'jbic-event-view',
      meta: {
        authRequired: true,
        module: 'jbic_management-groups',
        instanceType: EntityTypes.GROUP
      },
      component: () => import('./views/JBIC/components/EventView.vue')
    },
    /**
     * Non module pages below
     */
    {
      path: '/access-forbidden',
      name: 'access-forbidden',
      meta: {
        module: 'non_module'
      },
      component: () => import('./views/AccessForbidden.vue')
    },
    {
      path: '*',
      name: 'page-not-found',
      meta: {
        authRequired: false,
        module: 'non_module'
      },
      component: () => import('./views/PageNotFound.vue')
    }
  ]
});

export const myJBINavbarLinks: CustomNavLink[] = [
  {
    label: 'Account',
    to: {
      name: 'UserProfile',
      query: { tab: 'profile' },
      module: 'account'
    }
  },
  {
    label: 'User Administration',
    to: {
      name: 'admin-user-management',
      module: 'user_administration',
      query: { tab: 'Users' }
    }
  },
  {
    label: 'Group Administration',
    to: {
      name: 'admin-group-management',
      module: 'group_administration',
      query: { tab: 'Groups' },
      instanceType: EntityTypes.GROUP
    }
  },
  {
    label: 'Product Administration',
    to: {
      name: 'admin-product-management',
      module: 'product_administration'
    }
  },
  {
    label: 'Roles and Permissions',
    to: {
      name: 'roles-and-permissions',
      module: 'roles_and_permissions',
      query: { tab: 'Roles' }
    }
  },
  {
    label: 'Third Party Management',
    to: {
      name: 'third-party-management',
      module: 'third_party_access_management'
    }
  },

  {
    label: 'Terms & Conditions',
    to: {
      name: 'terms-and-conditiions-management',
      module: 'terms_and_conditions'
    }
  },
  {
    label: 'Journals',
    to: {
      name: 'Journals',
      module: 'journals'
    }
  },
  {
    label: 'JBIC Management',
    to: {
      name: 'jbic-management',
      module: 'jbic_management-groups',
      instanceType: EntityTypes.GROUP
    }
  },
  {
    label: 'Transactions',
    to: {
      name: 'admin-audit-log',
      module: 'transactions'
    }
  },

  {
    label: 'Sign Out',
    url: `${process.env.VUE_APP_BACKEND_HOSTNAME}/logout`,
    class: 'has-text-primary has-text-weight-semibold'
  }
];

export const jtmsNavigationLinks: CustomNavLink[] = [
  {
    label: 'Course Templates',
    to: {
      name: 'jtms-course_template',
      module: 'jtms-course_template'
    }
  },
  {
    label: 'Course License Templates',
    to: {
      name: 'jtms-course_license_template',
      module: 'jtms-course_license_template'
    }
  },
  {
    label: 'Course Attributes',
    to: {
      name: 'jtms-course_attribute',
      module: 'jtms-course_attribute'
    }
  },
  {
    label: 'Course Modules',
    to: {
      name: 'jtms-course_module',
      module: 'jtms-course_module'
    }
  },
  {
    label: 'Registration Question Templates',
    to: {
      name: 'jtms-registration_question_template',
      module: 'jtms-registration_question_template'
    }
  },
  {
    label: 'Tasks',
    to: {
      name: 'jtms-tasks',
      module: 'jtms-tasks'
    }
  },
  {
    label: 'Certificate',
    to: {
      name: 'jtms-certificate',
      module: 'jtms-certificate'
    }
  },
  {
    label: 'Reports',
    to: {
      name: 'jtms-reports',
      module: 'jtms-reports'
    }
  },
  {
    label: "Trainer's Accreditation",
    to: {
      name: 'jtms-accreditation',
      module: 'jtms-accreditation'
    }
  },
  {
    label: 'MYJBI',
    to: {
      name: 'LandingPage',
      module: 'non_module'
    }
  },
  {
    label: 'Sign Out',
    url: `${process.env.VUE_APP_BACKEND_HOSTNAME}/logout`,
    class: 'has-text-primary has-text-weight-semibold'
  }
];

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-background');
  if (appLoading) {
    appLoading.style.display = 'none';
  }
});

router.beforeEach(SetContext);
router.beforeEach(GeneralRouteGuard);
router.beforeEach(UserAttributeRouteGuard);
router.beforeEach(CasbinRouteGuard);

export default router;
